import { useNavigate, useSearch } from '@tanstack/react-router';
import { BookOpen, LayoutList } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  BALL_IN_COURT_LABELS,
  COMMAND_FUNNEL_LABELS,
  COMMAND_FUNNEL_STAGES,
  JOB_STAGE_COLUMN_LABELS,
  METRIC_DICTIONARY_PATH,
  REQUISITION_PRIORITIES,
  REQUISITION_PRIORITY_LABELS,
  SLA_STATE_LABELS,
  fiscalQuarterMonths,
  fiscalYearLabel,
  monthLabel,
  type BreachRow,
  type CommandCenter,
  type CommandFunnelStage,
  type DrillMetric,
  type ForecastRow,
  type FunnelRow,
  type JobStageRow,
  type MetricKey,
  type MoneyFigure,
  type OpenRequisitionRow,
  type SourceRow,
  type TimeToFillMonth,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card, MetricStrip } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { sortRows, type SortReaders } from '../../../components/data-table/sort-rows';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { SkeletonLines } from '../../../components/Skeleton';
import { STAGE_TONES } from '../../../components/status-tones';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { TableSection } from '../../../components/TableSection';
import type { Tone } from '../../../components/Tone';
import { formatDate, formatDateTimeLong, formatNumber, formatRelative } from '../../../lib/format';
import { useModule, useModules } from '../../../lib/modules';
import { EMPTY_SELECTION, type SelectionState } from '../../../lib/selection';
import { router } from '../../../router';
import { useCanView } from '../../../stores/session';
import { DeepLink } from '../../notifications/components/DeepLink';
import { PriorityDot, SlaFigure, requisitionTone } from '../../requisitions/components/RequisitionBadges';
import { slaFigures, slaLimit } from '../../requisitions/sla-format';
import { drillHref, useCommandCenter, type CommandCenterUrl, type ReportUrl } from '../api/reports-api';
import { HowCounted } from '../components/AnalyticsBits';
import { ReportFilters } from '../components/ReportFilters';
import { days, furthestStageText, moneyList, percent, periodText, shownJobStageColumns, workingDaysText } from '../report-format';
import styles from '../Reports.module.css';
import { METRIC_TONES } from '../metric-tones';

const sectionId = (section: string): string => `command-${section}`;

/**
 * RPT-01 — the HR Lead command center (master plan S28, E15 screen 1). Every figure is computed
 * from the event log when asked, every count opens the records behind it (F17.02, 06-qol §12),
 * and the page says which metric dictionary it was computed under.
 */
export function CommandCenterPage(): ReactNode {
  const canView = useCanView('reports_all_staff');
  const search: CommandCenterUrl = useSearch({ from: '/app-layout/reports/command-center' });
  const navigate = useNavigate();
  const center = useCommandCenter(search, canView);
  const data = center.data;

  // N-70 / N-71 open a section directly (Q18).
  const section = search.section;
  const hasData = data !== undefined;
  useEffect(() => {
    if (section && hasData) document.getElementById(sectionId(section))?.scrollIntoView({ block: 'start' });
  }, [section, hasData]);

  if (!canView) return <ForbiddenState resource="the command center" contact="Your admin" />;
  if (center.error && !data) return <ServerErrorState onRetry={() => void center.refetch()} />;

  const setFilters = (next: ReportUrl): void => void navigate({ to: '/reports/command-center', search: { ...next, ...(search.convert ? { convert: search.convert } : {}) }, replace: true });
  const setConverted = (isOn: boolean): void => void navigate({ to: '/reports/command-center', search: { ...search, convert: isOn ? true : undefined }, replace: true });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <h1 className={styles.title}>Command center</h1>
          <p className={styles.lead}>
            All hiring, everywhere, computed from the event log.
            {data && (
              <span className={styles.meta} title={formatDateTimeLong(data.generatedAt)}>
                {' '}
                Updated {formatRelative(data.generatedAt)}.
              </span>
            )}
          </p>
        </div>
        <span className={styles.headerActions}>
          <Checkbox checked={search.convert === true} onChange={setConverted} label="Show converted" />
          <Button variant="outline" icon={LayoutList} onClick={() => router.history.push('/workspace')}>
            My workspace
          </Button>
          <Button variant="ghost" icon={BookOpen} onClick={() => router.history.push(METRIC_DICTIONARY_PATH)}>
            Metric definitions
          </Button>
        </span>
      </header>

      {data && <ReportFilters filters={data.filters} onChange={setFilters} />}

      {!data ? (
        <Card>
          <SkeletonLines count={6} />
        </Card>
      ) : (
        <Sections data={data} />
      )}
    </div>
  );
}

function Sections({ data }: { readonly data: CommandCenter }): ReactNode {
  const filters = data.filters;
  // Rule 13 (bug B9): breaches, their tile and their table belong to Time limits.
  const isSlaOn = useModule('sla');
  const go = (metric: DrillMetric, key = ''): (() => void) => () => router.history.push(drillHref(filters, metric, key));
  return (
    <>
      <p className={styles.meta}>
        {periodText(filters)} · computed under <DeepLink to={METRIC_DICTIONARY_PATH} className={styles.inlineLink}>metric dictionary v{data.dictionaryVersion}</DeepLink> (published {formatDate(`${data.dictionaryPublishedOn}T00:00:00Z`)}).
      </p>

      <Card isFlush>
        <MetricStrip
          metrics={[
            { label: 'Open requisitions', value: formatNumber(data.openRequisitions.total), onClick: go('open_requisitions') },
            { label: 'Hires in the period', value: formatNumber(data.costPerHire.hires), onClick: go('hires') },
            { label: 'Time to fill (median, active)', value: workingDaysText(data.timeToFill.medianActiveDays), onClick: go('time_to_fill') },
            ...(isSlaOn ? [{ label: 'SLA breaches', value: formatNumber(data.breaches.total), onClick: go('sla_breaches') }] : []),
            { label: 'From talent pools', value: percent(data.poolSourced.share), onClick: go('pool_sourced') },
          ]}
        />
      </Card>

      <OpenRequisitions data={data} />
      <FunnelByJob data={data} />
      <StageConversion data={data} />

      {isSlaOn && <Breaches data={data} />}

      <div className={styles.grid}>
        <TimeToFill data={data} />

        <Section id="cost" title="Cost per hire" meta="Each currency on its own" definition="cost_per_hire">
          <dl className={styles.facts}>
            <dt>Hires</dt>
            <dd>
              <Count value={data.costPerHire.hires} href={drillHref(filters, 'hires')} />
            </dd>
            <dt>Spend</dt>
            <dd>
              {data.costPerHire.spend.length === 0
                ? '—'
                : data.costPerHire.spend.map((figure) => (
                    <DeepLink key={figure.currency} to={drillHref(filters, 'cost_per_hire', figure.currency)} className={styles.count}>
                      {moneyList([figure])}
                    </DeepLink>
                  ))}
            </dd>
            <dt>Per hire</dt>
            <dd className={styles.figure}>{moneyList(data.costPerHire.perHire)}</dd>
            {data.converted && (
              <>
                <dt>All in {data.converted.currency}</dt>
                <dd>
                  {moneyList([data.converted.spend])}
                  <span className={styles.cellMeta}> · {data.converted.perHire ? `${moneyList([data.converted.perHire])} per hire` : 'no hires'}</span>
                  {data.converted.unconverted.length > 0 && <span className={styles.cellMeta}> · not converted, no rate on the day: {moneyList(data.converted.unconverted)}</span>}
                </dd>
              </>
            )}
            <dt>From talent pools</dt>
            <dd>
              <Count value={data.poolSourced.fromPools} href={drillHref(filters, 'pool_sourced')} /> of {data.poolSourced.hires} · {percent(data.poolSourced.share)}
            </dd>
          </dl>
        </Section>
      </div>

      <Forecast data={data} />
      <Sources data={data} />
    </>
  );
}

function Section({ id, title, meta, definition, children }: { readonly id: string; readonly title: string; readonly meta: string; readonly definition: MetricKey; readonly children: ReactNode }): ReactNode {
  return (
    <div id={sectionId(id)} className={styles.section}>
      <Card
        title={title}
        meta={meta}
        tone={METRIC_TONES[definition]}
        action={
          <DeepLink to={`${METRIC_DICTIONARY_PATH}#${definition}`} className={styles.definition}>
            How this is counted
          </DeepLink>
        }
      >
        {children}
      </Card>
    </div>
  );
}

// --- The record lists, as tables (DECISIONS-LOG 2026-10-06: tables are tables, not cards) -------

/** Each stage in the tone its part of hiring wears on the pipeline board. */
const FUNNEL_STAGE_TONES: Readonly<Record<CommandFunnelStage, Tone>> = {
  applied: STAGE_TONES.new,
  screening: STAGE_TONES.screening,
  assessment: STAGE_TONES.assessment,
  interview: STAGE_TONES.interview_1,
  offer: STAGE_TONES.offer,
  hired: STAGE_TONES.hired_pending_join,
  joined: STAGE_TONES.joined,
};

const REQUISITION_SORT: SortReaders<OpenRequisitionRow> = {
  requisition: (row) => row.reference,
  priority: (row) => REQUISITION_PRIORITIES.indexOf(row.priority),
  department: (row) => departmentText(row),
  recruiter: (row) => row.recruiterName ?? '',
  elapsed: (row) => row.elapsedDays ?? -1,
  active: (row) => row.activeDays ?? -1,
  pipeline: (row) => row.candidatesInPipeline,
  furthest: (row) => (row.furthestStage ? COMMAND_FUNNEL_STAGES.indexOf(row.furthestStage) : -1),
  waiting: (row) => waitingOnText(row),
  sla: (row) => row.applicationsAtRisk,
};

function requisitionColumns(isOffersOn: boolean): readonly Column<OpenRequisitionRow>[] {
  return [
    {
      id: 'requisition',
      header: 'Requisition',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 280,
      cell: (row) => (
        <DeepLink to={row.href} className={styles.rowLink}>
          {row.reference} · {row.title}
        </DeepLink>
      ),
      exportValue: (row) => `${row.reference} · ${row.title}`,
    },
    { id: 'priority', header: 'Priority', isSortable: true, defaultWidth: 110, cell: (row) => <PriorityDot priority={row.priority} />, exportValue: (row) => REQUISITION_PRIORITY_LABELS[row.priority] },
    { id: 'department', header: 'Department', isSortable: true, defaultWidth: 200, cell: (row) => departmentText(row) },
    { id: 'recruiter', header: 'Recruiter', isSortable: true, defaultWidth: 160, cell: (row) => row.recruiterName },
    { id: 'elapsed', header: 'Days open', isNumeric: true, isSortable: true, defaultWidth: 110, cell: (row) => (row.elapsedDays === null ? null : days(row.elapsedDays)) },
    { id: 'active', header: 'Active', isNumeric: true, isSortable: true, defaultWidth: 150, cell: (row) => (row.activeDays === null ? null : workingDaysText(row.activeDays)) },
    { id: 'pipeline', header: 'Pipeline', isNumeric: true, isSortable: true, defaultWidth: 100, cell: (row) => formatNumber(row.candidatesInPipeline) },
    { id: 'furthest', header: 'Furthest stage', isSortable: true, defaultWidth: 150, cell: (row) => furthestStageText(row, isOffersOn) },
    { id: 'waiting', header: 'Waiting on', isSortable: true, defaultWidth: 150, cell: (row) => waitingOnText(row) },
    {
      id: 'sla',
      header: 'SLA',
      isSortable: true,
      defaultWidth: 300,
      cell: (row) =>
        row.sla === null && row.applicationsAtRisk === 0 ? null : (
          <span className={styles.badges}>
            {row.sla && <SlaFigure sla={row.sla} />}
            {row.applicationsAtRisk > 0 && <Chip tone="warning">{row.applicationsAtRisk} at risk</Chip>}
          </span>
        ),
      exportValue: (row) => [row.sla ? `${SLA_STATE_LABELS[row.sla.state]} — ${slaFigures(row.sla)}` : '', row.applicationsAtRisk > 0 ? `${row.applicationsAtRisk} at risk` : ''].filter(Boolean).join(' · '),
    },
  ];
}

function OpenRequisitions({ data }: { readonly data: CommandCenter }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const isOffersOn = useModule('offers');
  const isSlaOn = useModule('sla');
  const items = data.openRequisitions.items;
  const rows = sortRows(items, sort, REQUISITION_SORT);
  return (
    <TableSection id={sectionId('requisitions')} title="Open requisitions" meta={`${data.openRequisitions.total} open · as they stand now`} action={<HowCounted metric="requisition_age" />}>
      {items.length === 0 ? (
        <p className={styles.empty}>No open requisitions under these filters.</p>
      ) : (
        <DataTable
          screenId="command-open-requisitions"
          label="Open requisitions"
          columns={isSlaOn ? requisitionColumns(isOffersOn) : requisitionColumns(isOffersOn).filter((column) => column.id !== 'sla')}
          rows={rows}
          toneOf={(row) => requisitionTone(row.status, isSlaOn ? row.sla : null)}
          getRowId={(row) => row.id}
          total={rows.length}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          onOpenRow={(row) => router.history.push(row.href)}
          linkOf={(row) => row.href}
          exportName="open-requisitions"
        />
      )}
    </TableSection>
  );
}

function FunnelByJob({ data }: { readonly data: CommandCenter }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const filters = data.filters;
  const { items, totals } = data.jobsByStage;
  const stages = shownJobStageColumns(useModules());
  const columns: readonly Column<JobStageRow>[] = [
    {
      id: 'job',
      header: 'Job',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 240,
      cell: (row) => (
        <DeepLink to={pipelineHref(row)} className={styles.rowLink}>
          {row.title}
        </DeepLink>
      ),
      exportValue: (row) => row.title,
    },
    { id: 'reference', header: 'Reference', isSortable: true, defaultWidth: 120, cell: (row) => row.reference },
    { id: 'recruiter', header: 'Recruiter', isSortable: true, defaultWidth: 160, cell: (row) => row.recruiterName },
    ...stages.map(
      (stage): Column<JobStageRow> => ({
        id: stage,
        header: JOB_STAGE_COLUMN_LABELS[stage],
        isNumeric: true,
        isSortable: true,
        defaultWidth: 110,
        cell: (row) => <Count value={row.counts[stage]} href={drillHref(filters, 'jobs_by_stage', `${row.jobId}:${stage}`)} />,
        exportValue: (row) => row.counts[stage],
      }),
    ),
  ];
  const readers: SortReaders<JobStageRow> = {
    job: (row) => row.title,
    reference: (row) => row.reference,
    recruiter: (row) => row.recruiterName ?? '',
    ...Object.fromEntries(stages.map((stage) => [stage, (row: JobStageRow): number => row.counts[stage]])),
  };
  const rows = sortRows(items, sort, readers);
  return (
    <TableSection id={sectionId('stages')} title="Jobs by stage" meta={`${items.length} live jobs · as they stand now`} action={<HowCounted metric="jobs_by_stage" />}>
      {items.length === 0 ? (
        <p className={styles.empty}>No live jobs under these filters.</p>
      ) : (
        <div className={styles.tableBlock}>
          <DataTable
            screenId="command-jobs-by-stage"
            label="Jobs by stage"
            columns={columns}
            rows={rows}
            getRowId={(row) => row.jobId}
            total={rows.length}
            sort={sort}
            onSortChange={setSort}
            selection={selection}
            onSelectionChange={setSelection}
            onOpenRow={(row) => router.history.push(pipelineHref(row))}
            linkOf={pipelineHref}
            exportName="jobs-by-stage"
          />
          {/* The table has no footer row, so the totals sit directly under it, each still opening its records. */}
          <p className={styles.totals}>
            <span className={styles.totalsLabel}>All live jobs</span>
            {stages.map((stage) => (
              <span key={stage} className={styles.totalItem}>
                {JOB_STAGE_COLUMN_LABELS[stage]} <Count value={totals[stage]} href={drillHref(filters, 'jobs_by_stage', `all:${stage}`)} />
              </span>
            ))}
          </p>
        </div>
      )}
    </TableSection>
  );
}

const FUNNEL_SORT: SortReaders<FunnelRow> = {
  stage: (row) => COMMAND_FUNNEL_STAGES.indexOf(row.stage),
  entered: (row) => row.entered,
  advanced: (row) => row.advanced,
  rejected: (row) => row.rejected,
  withdrawn: (row) => row.withdrawn,
  conversion: (row) => row.conversion ?? -1,
  medianDays: (row) => row.medianDays ?? -1,
};

type FunnelCount = 'entered' | 'advanced' | 'rejected' | 'withdrawn';

function StageConversion({ data }: { readonly data: CommandCenter }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const filters = data.filters;
  const countColumn = (id: FunnelCount, header: string): Column<FunnelRow> => ({
    id,
    header,
    isNumeric: true,
    isSortable: true,
    defaultWidth: 110,
    cell: (row) => <Count value={row[id]} href={drillHref(filters, 'funnel', `${row.stage}:${id}`)} />,
    exportValue: (row) => row[id],
  });
  const columns: readonly Column<FunnelRow>[] = [
    { id: 'stage', header: 'Stage', isIdentity: true, isSortable: true, defaultWidth: 180, cell: (row) => COMMAND_FUNNEL_LABELS[row.stage] },
    countColumn('entered', 'Entered'),
    countColumn('advanced', 'Advanced'),
    countColumn('rejected', 'Rejected'),
    countColumn('withdrawn', 'Withdrew'),
    { id: 'conversion', header: 'Conversion', isNumeric: true, isSortable: true, defaultWidth: 120, cell: (row) => (row.conversion === null ? null : percent(row.conversion)) },
    { id: 'medianDays', header: 'Median days', isNumeric: true, isSortable: true, defaultWidth: 120, cell: (row) => row.medianDays },
  ];
  const rows = sortRows(data.funnel, sort, FUNNEL_SORT);
  return (
    <TableSection id={sectionId('funnel')} title="Funnel for the period" meta={periodText(filters)} action={<HowCounted metric="funnel" />}>
      <DataTable
        screenId="command-funnel"
        label="Funnel for the period"
        columns={columns}
        rows={rows}
        toneOf={(row) => FUNNEL_STAGE_TONES[row.stage]}
        getRowId={(row) => row.stage}
        total={rows.length}
        sort={sort}
        onSortChange={setSort}
        selection={selection}
        onSelectionChange={setSelection}
        isSelectable={false}
        exportName="funnel-for-the-period"
      />
    </TableSection>
  );
}

/** A breach still running needs someone now; one that has been resolved is done. */
const BREACH_TONES = { open: 'urgent', resolved: 'done' } as const satisfies Readonly<Record<string, Tone>>;

const BREACH_SORT: SortReaders<BreachRow> = {
  clock: (row) => row.label,
  detail: (row) => row.detail,
  responsible: (row) => row.responsibleName ?? '',
  waiting: (row) => (row.ballInCourt ? BALL_IN_COURT_LABELS[row.ballInCourt] : ''),
  breached: (row) => row.breachedAt,
  sla: (row) => row.sla.activeMinutes,
  status: (row) => (row.isOpen ? 0 : 1),
};

const BREACH_COLUMNS: readonly Column<BreachRow>[] = [
  {
    id: 'clock',
    header: 'Breach',
    isIdentity: true,
    isSortable: true,
    defaultWidth: 260,
    cell: (row) => (
      <DeepLink to={row.href} className={styles.rowLink}>
        {row.label}
      </DeepLink>
    ),
    exportValue: (row) => row.label,
  },
  { id: 'detail', header: 'Detail', isSortable: true, defaultWidth: 240, cell: (row) => row.detail },
  { id: 'responsible', header: 'Responsible', isSortable: true, defaultWidth: 160, cell: (row) => row.responsibleName },
  { id: 'waiting', header: 'Waiting on', isSortable: true, defaultWidth: 150, cell: (row) => (row.ballInCourt ? BALL_IN_COURT_LABELS[row.ballInCourt] : null) },
  {
    id: 'breached',
    header: 'Breached',
    isSortable: true,
    defaultWidth: 140,
    cell: (row) => <span title={formatDateTimeLong(row.breachedAt)}>{formatRelative(row.breachedAt)}</span>,
    exportValue: (row) => formatDateTimeLong(row.breachedAt),
  },
  {
    id: 'sla',
    header: 'SLA',
    isSortable: true,
    defaultWidth: 340,
    cell: (row) => <SlaFigure sla={row.sla} showLimit />,
    exportValue: (row) => [`${SLA_STATE_LABELS[row.sla.state]} — ${slaFigures(row.sla)}`, slaLimit(row.sla)].filter(Boolean).join(' · '),
  },
  { id: 'status', header: 'Status', isSortable: true, defaultWidth: 120, cell: (row) => (row.isOpen ? 'Still open' : <Chip>Resolved</Chip>), exportValue: (row) => (row.isOpen ? 'Still open' : 'Resolved') },
];

/** The ball-in-court figures stay as tiles (they are totals); the breaches under them are a table. */
function Breaches({ data }: { readonly data: CommandCenter }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const filters = data.filters;
  const items = data.breaches.items;
  return (
    <TableSection id={sectionId('breaches')} title="SLA breaches" meta={`${data.breaches.total} in the period · ${data.breaches.stillOpen} still open`} action={<HowCounted metric="sla_breaches" />}>
      {data.breaches.total === 0 ? (
        <p className={styles.empty}>Nothing passed its time limit in this period.</p>
      ) : (
        <>
          <div className={styles.courts}>
            {data.breaches.byCourt.map((row) => (
              <DeepLink key={row.ballInCourt} to={drillHref(filters, 'sla_breaches', row.ballInCourt)} className={styles.court}>
                <span className={styles.figure}>{row.breaches}</span>
                <span>{row.ballInCourt === 'unknown' ? 'Not recorded' : BALL_IN_COURT_LABELS[row.ballInCourt]}</span>
                <span className={styles.cellMeta}>{row.stillOpen} still open</span>
              </DeepLink>
            ))}
          </div>
          <DataTable
            screenId="command-sla-breaches"
            label="SLA breaches"
            columns={BREACH_COLUMNS}
            rows={sortRows(items, sort, BREACH_SORT)}
            toneOf={(row) => (row.isOpen ? BREACH_TONES.open : BREACH_TONES.resolved)}
            getRowId={(row) => row.clockId}
            total={items.length}
            sort={sort}
            onSortChange={setSort}
            selection={selection}
            onSelectionChange={setSelection}
            onOpenRow={(row) => router.history.push(row.href)}
            linkOf={(row) => row.href}
            exportName="sla-breaches"
          />
        </>
      )}
    </TableSection>
  );
}

const TIME_TO_FILL_SORT: SortReaders<TimeToFillMonth> = {
  month: (row) => row.month,
  fills: (row) => row.fills,
  elapsed: (row) => row.medianElapsedDays ?? -1,
  active: (row) => row.medianActiveDays ?? -1,
};

/** The medians stay as the headline above the table; the months are the table. */
function TimeToFill({ data }: { readonly data: CommandCenter }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const filters = data.filters;
  const { trend } = data.timeToFill;
  const columns: readonly Column<TimeToFillMonth>[] = [
    { id: 'month', header: 'Month', isIdentity: true, isSortable: true, defaultWidth: 140, cell: (row) => monthName(row.month) },
    {
      id: 'fills',
      header: 'Filled',
      isNumeric: true,
      isSortable: true,
      defaultWidth: 100,
      cell: (row) => <Count value={row.fills} href={drillHref(filters, 'time_to_fill', row.month)} />,
      exportValue: (row) => row.fills,
    },
    { id: 'elapsed', header: 'Elapsed', isNumeric: true, isSortable: true, defaultWidth: 110, cell: (row) => (row.medianElapsedDays === null ? null : days(row.medianElapsedDays)) },
    { id: 'active', header: 'Active', isNumeric: true, isSortable: true, defaultWidth: 150, cell: (row) => (row.medianActiveDays === null ? null : workingDaysText(row.medianActiveDays)) },
  ];
  return (
    <TableSection id={sectionId('time-to-fill')} title="Time to fill" meta={`${data.timeToFill.fills} filled in the period`} action={<HowCounted metric="time_to_fill" />}>
      <p className={styles.headline}>
        <span className={styles.figure}>{days(data.timeToFill.medianElapsedDays)}</span> elapsed · <span className={styles.figure}>{workingDaysText(data.timeToFill.medianActiveDays)}</span> active <span className={styles.cellMeta}>(medians)</span>
      </p>
      {trend.length === 0 ? (
        <p className={styles.empty}>Nothing was filled in this period.</p>
      ) : (
        <DataTable
          screenId="command-time-to-fill"
          label="Time to fill by month"
          columns={columns}
          rows={sortRows(trend, sort, TIME_TO_FILL_SORT)}
          getRowId={(row) => row.month}
          total={trend.length}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={false}
          exportName="time-to-fill"
        />
      )}
    </TableSection>
  );
}

type ForecastCount = 'plannedThisQuarter' | 'approvedThisQuarter' | 'plannedNextQuarter' | 'approvedNextQuarter' | 'filled' | 'vacant' | 'inRecruitment' | 'openRequisitions';

const FORECAST_SORT: SortReaders<ForecastRow> = {
  department: (row) => row.departmentName,
  venture: (row) => row.ventureName,
  plannedThisQuarter: (row) => row.plannedThisQuarter,
  approvedThisQuarter: (row) => row.approvedThisQuarter,
  plannedNextQuarter: (row) => row.plannedNextQuarter,
  approvedNextQuarter: (row) => row.approvedNextQuarter,
  filled: (row) => row.filled,
  vacant: (row) => row.vacant,
  inRecruitment: (row) => row.inRecruitment,
  openRequisitions: (row) => row.openRequisitions,
};

const HEADCOUNT_PATH = '/organisation/headcount';

function Forecast({ data }: { readonly data: CommandCenter }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const { forecast } = data;
  const countColumn = (id: ForecastCount, header: string): Column<ForecastRow> => ({
    id,
    header,
    isNumeric: true,
    isSortable: true,
    defaultWidth: 130,
    cell: (row) => formatNumber(row[id]),
    exportValue: (row) => row[id],
  });
  const columns: readonly Column<ForecastRow>[] = [
    {
      id: 'department',
      header: 'Department',
      isIdentity: true,
      isSortable: true,
      defaultWidth: 200,
      cell: (row) => (
        <DeepLink to={HEADCOUNT_PATH} className={styles.rowLink}>
          {row.departmentName}
        </DeepLink>
      ),
      exportValue: (row) => row.departmentName,
    },
    { id: 'venture', header: 'Venture', isSortable: true, defaultWidth: 150, cell: (row) => row.ventureName },
    countColumn('plannedThisQuarter', `Q${forecast.quarter} planned`),
    countColumn('approvedThisQuarter', `Q${forecast.quarter} approved`),
    countColumn('plannedNextQuarter', `Q${forecast.nextQuarter} planned`),
    countColumn('approvedNextQuarter', `Q${forecast.nextQuarter} approved`),
    countColumn('filled', 'Filled'),
    countColumn('vacant', 'Vacant'),
    countColumn('inRecruitment', 'In recruitment'),
    countColumn('openRequisitions', 'Open requisitions'),
  ];
  return (
    <TableSection
      id={sectionId('forecast')}
      title="Headcount forecast"
      meta={`${fiscalYearLabel(forecast.fiscalYear)} Q${forecast.quarter} (${fiscalQuarterMonths(forecast.quarter)}) and the quarter after`}
      action={<HowCounted metric="headcount_forecast" />}
    >
      {forecast.items.length === 0 ? (
        <p className={styles.empty}>No departments under these filters.</p>
      ) : (
        <DataTable
          screenId="command-headcount-forecast"
          label="Headcount forecast"
          columns={columns}
          rows={sortRows(forecast.items, sort, FORECAST_SORT)}
          getRowId={(row) => row.departmentId}
          total={forecast.items.length}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={false}
          onOpenRow={() => router.history.push(HEADCOUNT_PATH)}
          exportName="headcount-forecast"
        />
      )}
    </TableSection>
  );
}

type SourceCount = 'applications' | 'qualified' | 'interviewed' | 'hired';

const SOURCE_SORT: SortReaders<SourceRow> = {
  source: (row) => row.label,
  applications: (row) => row.applications,
  qualified: (row) => row.qualified,
  qualifiedRate: (row) => row.qualifiedRate ?? -1,
  interviewed: (row) => row.interviewed,
  hired: (row) => row.hired,
  hireRate: (row) => row.hireRate ?? -1,
};

function Sources({ data }: { readonly data: CommandCenter }): ReactNode {
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);
  const filters = data.filters;
  const countColumn = (id: SourceCount, header: string): Column<SourceRow> => ({
    id,
    header,
    isNumeric: true,
    isSortable: true,
    defaultWidth: 130,
    cell: (row) => <Count value={row[id]} href={drillHref(filters, 'source_effectiveness', `${row.source}:${id}`)} />,
    exportValue: (row) => row[id],
  });
  const money = (figures: readonly MoneyFigure[]): string | null => (figures.length === 0 ? null : moneyList(figures));
  const columns: readonly Column<SourceRow>[] = [
    { id: 'source', header: 'Source', isIdentity: true, isSortable: true, defaultWidth: 180, cell: (row) => row.label },
    countColumn('applications', 'Applications'),
    countColumn('qualified', 'Qualified'),
    { id: 'qualifiedRate', header: 'Qualified %', isNumeric: true, isSortable: true, defaultWidth: 120, cell: (row) => (row.qualifiedRate === null ? null : percent(row.qualifiedRate)) },
    countColumn('interviewed', 'Interviewed'),
    countColumn('hired', 'Hired'),
    { id: 'hireRate', header: 'Hire %', isNumeric: true, isSortable: true, defaultWidth: 100, cell: (row) => (row.hireRate === null ? null : percent(row.hireRate)) },
    // Money is never sorted: a source can hold several currencies, never added together (Q17).
    { id: 'spend', header: 'Spend', isNumeric: true, defaultWidth: 180, cell: (row) => money(spendOf(data, row.source, row.spend)) },
    { id: 'perApplication', header: 'Per application', isNumeric: true, defaultWidth: 160, cell: (row) => money(row.costPerApplication) },
    { id: 'perHire', header: 'Per hire', isNumeric: true, defaultWidth: 160, cell: (row) => money(perHireOf(data, row.source, row.costPerHire)) },
  ];
  return (
    <TableSection id={sectionId('sources')} title="Source effectiveness" meta={`Applications made in the period · ${periodText(filters)}`} action={<HowCounted metric="source_effectiveness" />}>
      {data.sources.length === 0 ? (
        <p className={styles.empty}>No applications in this period.</p>
      ) : (
        <DataTable
          screenId="command-sources"
          label="Source effectiveness"
          columns={columns}
          rows={sortRows(data.sources, sort, SOURCE_SORT)}
          getRowId={(row) => row.source}
          total={data.sources.length}
          sort={sort}
          onSortChange={setSort}
          selection={selection}
          onSelectionChange={setSelection}
          isSelectable={false}
          exportName="source-effectiveness"
        />
      )}
    </TableSection>
  );
}

function pipelineHref(row: JobStageRow): string {
  return `/jobs/${row.jobId}/pipeline`;
}

function departmentText(row: OpenRequisitionRow): string {
  return [row.departmentName, row.ventureName].filter(Boolean).join(' · ');
}

function waitingOnText(row: OpenRequisitionRow): string {
  return row.ballInCourt ? BALL_IN_COURT_LABELS[row.ballInCourt] : 'On hold';
}

/** A figure that opens its records; a nought has none to open. */
function Count({ value, href }: { readonly value: number; readonly href: string }): ReactNode {
  if (value === 0) return <span className={styles.zero}>0</span>;
  return (
    <DeepLink to={href} className={styles.count}>
      {formatNumber(value)}
    </DeepLink>
  );
}

/** "2026-10" → "Oct 2026". */
function monthName(month: string): string {
  const [year, number] = month.split('-');
  return `${monthLabel(Number(number))} ${year}`;
}


/** S31 (Q21): with "Show converted" on, a source's spend in the reporting currency; otherwise per currency. */
function spendOf(data: CommandCenter, source: string, own: readonly MoneyFigure[]): readonly MoneyFigure[] {
  const converted = data.converted?.bySource.find((row) => row.source === source);
  return converted ? [converted.spend] : own;
}

function perHireOf(data: CommandCenter, source: string, own: readonly MoneyFigure[]): readonly MoneyFigure[] {
  const converted = data.converted?.bySource.find((row) => row.source === source);
  if (!converted) return own;
  return converted.costPerHire ? [converted.costPerHire] : [];
}
